import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ChevronRight, Folder, FolderPlus, Upload, Download, Trash2, Search, FileText, Home, MoreVertical } from "lucide-react";
import { useDriveAuth } from "../context/DriveAuthContext";
import {
  fetchFolders,
  fetchFiles,
  createFolder,
  uploadFile,
  downloadFile,
  deleteFile,
  deleteFolder,
  formatBytes,
} from "../lib/driveClient";

export default function DriveFiles() {
  const { isSuperadmin, isStaff } = useDriveAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [currentId, setCurrentId] = useState(null); // null = root
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const uploadRef = useRef(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [f, fi] = await Promise.all([fetchFolders(), fetchFiles(null)]);
      setFolders(f);
      setFiles(fi);
    } catch (e) {
      toast.error(e.message || "Konnte Daten nicht laden");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    loadAll();
  }, []);

  // Handle sidebar quick-actions via query params
  useEffect(() => {
    const action = searchParams.get("action");
    if (!action) return;
    if (action === "new-folder") {
      setShowNewFolder(true);
    } else if (action === "upload") {
      // Auto-open file picker after data loaded
      setTimeout(() => uploadRef.current?.click(), 300);
    }
    // Clear the query param after handling
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  const currentFolder = useMemo(() => folders.find((f) => f.id === currentId) || null, [folders, currentId]);
  const breadcrumbs = useMemo(() => {
    const trail = [];
    let cur = currentFolder;
    while (cur) {
      trail.unshift(cur);
      cur = folders.find((f) => f.id === cur.parent_id);
    }
    return trail;
  }, [currentFolder, folders]);

  const childFolders = useMemo(
    () => folders.filter((f) => f.parent_id === currentId).sort((a, b) => a.name.localeCompare(b.name)),
    [folders, currentId]
  );
  const folderFiles = useMemo(
    () =>
      files
        .filter((f) => f.folder_id === currentId)
        .filter((f) => (q ? f.name.toLowerCase().includes(q.toLowerCase()) : true))
        .sort((a, b) => new Date(b.uploaded_at) - new Date(a.uploaded_at)),
    [files, currentId, q]
  );

  const globalMatches = useMemo(() => {
    if (!q) return [];
    return files
      .filter((f) => f.name.toLowerCase().includes(q.toLowerCase()))
      .slice(0, 30);
  }, [files, q]);

  const onCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    setBusy(true);
    try {
      await createFolder({ name: newFolderName.trim(), parent_id: currentId, scope: "company" });
      setNewFolderName("");
      setShowNewFolder(false);
      await loadAll();
      toast.success("Ordner erstellt");
    } catch (e) {
      toast.error(e.message || "Ordner konnte nicht erstellt werden");
    } finally {
      setBusy(false);
    }
  };

  const onUpload = async (e) => {
    const list = Array.from(e.target.files || []);
    if (list.length === 0) return;
    if (!currentId) {
      toast.error("Bitte zuerst in einen Ordner navigieren");
      return;
    }
    setBusy(true);
    try {
      for (const file of list) {
        await uploadFile(currentId, file);
      }
      toast.success(`${list.length} Datei${list.length > 1 ? "en" : ""} hochgeladen`);
      await loadAll();
    } catch (err) {
      toast.error(err.message || "Upload fehlgeschlagen");
    } finally {
      setBusy(false);
      if (uploadRef.current) uploadRef.current.value = "";
    }
  };

  const onDelete = async (file) => {
    if (!window.confirm(`„${file.name}" wirklich löschen?`)) return;
    setBusy(true);
    try {
      await deleteFile(file);
      toast.success("Datei gelöscht");
      await loadAll();
    } catch (e) {
      toast.error(e.message || "Löschen fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const onDeleteFolder = async (folder) => {
    // Check ob Inhalt drin ist
    const hasFiles = files.some((f) => f.folder_id === folder.id);
    const hasSubs = folders.some((f) => f.parent_id === folder.id);
    const isEmpty = !hasFiles && !hasSubs;
    let withContent = false;
    if (!isEmpty) {
      const ok = window.confirm(
        `Der Ordner „${folder.name}" enthält Dateien oder Unterordner.\n\n` +
        `OK  → Ordner + gesamten Inhalt unwiederbringlich löschen\n` +
        `Abbrechen → Nichts löschen`
      );
      if (!ok) return;
      withContent = true;
    } else {
      const ok = window.confirm(`Ordner „${folder.name}" wirklich löschen?`);
      if (!ok) return;
    }
    setBusy(true);
    try {
      await deleteFolder(folder.id, { withContent });
      toast.success("Ordner gelöscht");
      if (currentId === folder.id) setCurrentId(folder.parent_id || null);
      await loadAll();
    } catch (e) {
      toast.error(e.message || "Löschen fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const canWrite = isStaff || isSuperadmin;

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <div className="flex items-center gap-3 mb-3 text-[10px] tracking-[0.4em] uppercase text-zinc-500 font-bold">
          <span className="block w-8 h-px bg-[#ff0033] shadow-[0_0_8px_#ff0033]" />
          Dateien
        </div>
        <h1 className="font-display font-black uppercase tracking-tighter leading-none text-3xl md:text-4xl">
          Dokumente & Uploads
        </h1>
      </motion.div>

      {/* Breadcrumb + Actions */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <nav className="flex items-center flex-wrap gap-1 text-sm">
          <button
            onClick={() => setCurrentId(null)}
            data-testid="crumb-root"
            className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-white transition-colors"
          >
            <Home size={12} /> Root
          </button>
          {breadcrumbs.map((b) => (
            <span key={b.id} className="inline-flex items-center gap-1">
              <ChevronRight size={12} className="text-zinc-600" />
              <button
                onClick={() => setCurrentId(b.id)}
                className="text-zinc-400 hover:text-white transition-colors"
              >
                {b.name}
              </button>
            </span>
          ))}
        </nav>

        {canWrite && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowNewFolder((s) => !s)}
              data-testid="btn-new-folder"
              className="inline-flex items-center gap-2 px-3 py-2 text-[10px] font-bold tracking-[0.2em] uppercase border border-white/15 hover:border-[#ff0033]/50 hover:text-white text-zinc-300 transition-colors"
            >
              <FolderPlus size={12} /> Ordner
            </button>
            <input ref={uploadRef} type="file" multiple hidden onChange={onUpload} data-testid="upload-input" />
            <button
              onClick={() => uploadRef.current?.click()}
              disabled={!currentId || busy}
              data-testid="btn-upload"
              className="inline-flex items-center gap-2 px-3 py-2 text-[10px] font-bold tracking-[0.2em] uppercase bg-white text-black hover:bg-[#ff0033] hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Upload size={12} /> Upload
            </button>
          </div>
        )}
      </div>

      {/* Inline new folder */}
      {showNewFolder && (
        <div className="flex items-center gap-2 bg-[#0A0A0A] border border-white/10 px-4 py-3">
          <FolderPlus size={14} className="text-[#ff0033]" />
          <input
            autoFocus
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && onCreateFolder()}
            placeholder="Ordnername…"
            data-testid="new-folder-input"
            className="flex-1 bg-transparent border-none outline-none text-white text-sm"
          />
          <button
            onClick={onCreateFolder}
            disabled={busy}
            data-testid="new-folder-save"
            className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#ff0033] hover:text-white transition-colors"
          >
            Speichern
          </button>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Globale Suche über alle Dateien…"
          data-testid="search-input"
          className="w-full bg-[#0A0A0A] border border-white/10 pl-11 pr-4 py-3 text-sm text-white outline-none focus:border-[#ff0033]/40 transition-colors"
        />
      </div>

      {/* Search Results (global) */}
      {q && globalMatches.length > 0 && currentId === null && (
        <div className="bg-[#0A0A0A] border border-[#ff0033]/30 p-4">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#ff0033] font-bold mb-3">
            Globale Treffer ({globalMatches.length})
          </div>
          <div className="divide-y divide-white/[0.04]">
            {globalMatches.map((f) => (
              <FileRow key={f.id} file={f} onDownload={() => downloadFile(f.storage_path, f.name)} onDelete={canWrite ? () => onDelete(f) : null} folders={folders} />
            ))}
          </div>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="p-10 text-center text-sm text-zinc-500">Lade…</div>
      ) : (
        <>
          {/* Folders */}
          {childFolders.length > 0 && (
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-3">Ordner</div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {childFolders.map((f) => (
                  <div
                    key={f.id}
                    data-testid={`folder-${f.slug}`}
                    className="group relative bg-[#0A0A0A] border border-white/[0.06] p-4 hover:border-[#ff0033]/40 hover:-translate-y-0.5 transition-all"
                  >
                    <button
                      onClick={() => setCurrentId(f.id)}
                      className="text-left w-full"
                    >
                      <Folder size={22} className="text-[#ff0033] mb-3" />
                      <div className="font-display font-bold uppercase text-sm tracking-wide truncate">{f.name}</div>
                      <div className="text-[9px] tracking-[0.25em] uppercase text-zinc-600 mt-1">{f.scope}</div>
                    </button>
                    {canWrite && (
                      <button
                        onClick={(e) => { e.stopPropagation(); onDeleteFolder(f); }}
                        data-testid={`folder-delete-${f.slug}`}
                        title="Ordner löschen"
                        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 p-2 text-zinc-500 hover:text-[#ff0033] hover:bg-[#ff0033]/[0.08] transition-all"
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Files */}
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-3 mt-2">
              Dateien in {currentFolder?.name || "Root"} ({folderFiles.length})
            </div>
            {folderFiles.length === 0 ? (
              <div className="bg-[#0A0A0A] border border-white/[0.06] p-10 text-center text-sm text-zinc-500">
                {currentId
                  ? canWrite
                    ? "Noch keine Dateien in diesem Ordner. Nutze den Upload-Button oben."
                    : "Noch keine Dateien."
                  : "Wähle einen Ordner links, um Dateien zu sehen."}
              </div>
            ) : (
              <div className="bg-[#0A0A0A] border border-white/[0.06] divide-y divide-white/[0.04]">
                {folderFiles.map((f) => (
                  <FileRow
                    key={f.id}
                    file={f}
                    onDownload={() => downloadFile(f.storage_path, f.name)}
                    onDelete={canWrite ? () => onDelete(f) : null}
                  />
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

const FileRow = ({ file, onDownload, onDelete, folders }) => {
  const folderName = folders?.find((f) => f.id === file.folder_id)?.name;
  return (
    <div className="p-4 md:px-5 flex items-center justify-between gap-4 hover:bg-white/[0.02] transition-colors">
      <div className="flex items-center gap-4 min-w-0 flex-1">
        <FileText size={16} className="text-zinc-500 shrink-0" />
        <div className="min-w-0">
          <div className="text-sm text-white truncate">{file.name}</div>
          <div className="text-[10px] tracking-[0.15em] uppercase text-zinc-600 mt-0.5">
            {formatBytes(file.size)} · {new Date(file.uploaded_at).toLocaleDateString("de-DE")}
            {folderName && <span className="ml-2 text-zinc-500">· {folderName}</span>}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          onClick={onDownload}
          data-testid="file-download"
          className="p-2 text-zinc-400 hover:text-white hover:bg-white/[0.05] transition-colors"
          title="Download"
        >
          <Download size={14} />
        </button>
        {onDelete && (
          <button
            onClick={onDelete}
            data-testid="file-delete"
            className="p-2 text-zinc-400 hover:text-[#ff0033] hover:bg-[#ff0033]/[0.1] transition-colors"
            title="Löschen"
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>
    </div>
  );
};
